import React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import { useDisplayVariant } from 'modules/productDetail'
import ProductImage from 'theme/atoms/ProductImage'
import useGallery from '../hooks/useGallery'
import SliderButton from 'theme/atoms/SliderButton'
import useTranslations from 'hooks/useTranslations'

type Props = {
  gallery: ReturnType<typeof useGallery>
}

export default function ThumbnailBar(props: Props) {
  const gallery = props.gallery
  const variant = useDisplayVariant()
  const t = useTranslations<'templates-PDP'>()
  return (
    <Wrapper
      className="ThumbnailBar"
      $showSlidePrevEvent={gallery.showSlidePrev}
      $showSlideNextEvent={gallery.showSlideNext}
      data-cy-collection="ThumbnailBar"
    >
      <div
        className="thumbnail-container"
        data-scroll-id="gallery-thumbnails"
        ref={gallery.thumbnailRef}
      >
        <div className="thumbnail-list">
          {gallery.thumbnails.map((img, index) => (
            <Thumbnail
              key={index}
              $isActive={index === gallery.index}
              onMouseOver={() => gallery.setIndex(index)}
              data-cy-handle="set-thumbnail"
            >
              {img.type === 'video' ? (
                // eslint-disable-next-line @kaminrunde/firescout/jsx-expression-needs-state
                <VideoThumbnail src={img.url} alt={variant.data.title} />
              ) : (
                <ProductImage img={img} alt={variant.data.title} size="s" />
              )}
            </Thumbnail>
          ))}
        </div>
        {gallery.showSlidePrev && (
          <SliderButton
            facing={'up'}
            title={t.asText(false, 'thumbnail_bar_previous')}
            onClick={() => gallery.slidePrev()}
            data-cy-handle="thumb-prev-button"
            data-cy-state="show-thumbs-prev"
          />
        )}
        {gallery.showSlideNext && (
          <SliderButton
            facing={'down'}
            title={t.asText(false, 'thumbnail_bar_next')}
            onClick={() => gallery.slideNext()}
            data-cy-handle="thumb-next-button"
            data-cy-state="show-thumbs-next"
          />
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div<{
  $showSlideNextEvent: boolean
  $showSlidePrevEvent: boolean
}>`
  order: 1;
  position: relative;
  display: none;

  @media (min-width: ${theme.ms.LG}px) {
    display: block;
  }

  > .thumbnail-container {
    overflow: scroll hidden;
    display: flex;
    justify-content: flex-start;
    height: 2.5rem;
    width: 0;
    min-width: 100%;
    @media (min-width: ${theme.ms.LG}px) {
      justify-content: center;
      overflow: hidden scroll;
      height: 0;
      min-height: 100%;
      min-width: 7.4375rem;
    }
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari and Opera */
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    > .thumbnail-list {
      display: flex;
      padding: 0.125rem;
      flex-direction: column;
      gap: ${theme.spacing('m')};

      &:after {
        content: '-';
        width: 0.0625rem;
        height: 0.0625rem;
        visibility: hidden;
        margin-top: -1.25rem;
      }
    }
  }
`

const Thumbnail = styled.div<{ $isActive?: boolean }>`
  cursor: pointer;
  aspect-ratio: 1/1;
  width: 100%;
  ${(p) => p.$isActive && `outline: 2px solid ${theme.colors.primary}`};
`
const VideoThumbnail = styled.img`
  aspect-ratio: 1/1;
  width: 100%;
`
